import { baseApi } from 'segments/api/baseApi';

import { adaptCourtsBooking, adaptCourtBooking } from '../utils/adapter';
import { transformAUCourtBkBody, transformCtCourtBkBody } from '../utils/transformer';

import type {
  CourtBooking,
  CourtsBooking,
  CreateCourtBooking,
  DeleteCourtBooking,
  GetCourtsBooking,
  AddUserToCourtBookingParams,
  UpdateCourtBookingParams,
} from './types';

export const api = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createCourtBooking: builder.mutation<unknown, CreateCourtBooking>({
      query: (formData: CreateCourtBooking) => ({
        url: '/admin/court_bookings/',
        method: 'POST',
        body: transformCtCourtBkBody(formData),
      }),
      invalidatesTags: ['CourtsBooking']
    }),
    getCourtsBooking: builder.query<CourtsBooking, GetCourtsBooking>({
      query: (params) => ({
        url: '/admin/court_bookings/table',
        method: 'GET',
        params: {
          'date_from': params.dateFrom,
          'date_to': params.dateTo
        }
      }),
      transformResponse: adaptCourtsBooking,
      providesTags: ['CourtsBooking'],
      keepUnusedDataFor: 1,
    }),
    getCourtBooking: builder.query<CourtBooking, number>({
      query: (courtId) => ({
        url: `/admin/court_bookings/${courtId}`,
        method: 'GET',
      }),
      transformResponse: adaptCourtBooking,
      providesTags: ['CourtsBooking'],
    }),
    deleteCourtBooking: builder.mutation<void, DeleteCourtBooking>({
      query: (params) => ({
        url: '/admin/court_bookings/',
        method: 'DELETE',
        params: {
          'booking_id': params.bookingId,
          ...(params.deleteUntil && { 'delete_until': params.deleteUntil })
        }
      }),
      invalidatesTags: ['CourtsBooking']
    }),
    addUserToCourtBooking: builder.mutation<unknown, AddUserToCourtBookingParams>({
      query: ({ bookingId, ...body }) => ({
        url: `/admin/court_bookings/${bookingId}/add_user`,
        method: 'PUT',
        body: transformAUCourtBkBody(body)
      }),
      invalidatesTags: ['CourtsBooking']
    }),
    updateCourtBooking: builder.mutation<void, UpdateCourtBookingParams>({
      query: ({ bookingId, courtId, note }) => ({
        url: `/admin/court_bookings/${bookingId}/update`,
        method: 'PUT',
        body: {
          'court_id': courtId,
          note
        }
      }),
      invalidatesTags: ['CourtsBooking']
    }),
    confirmCourtBooking: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/court_bookings/${id}/confirm`,
        method: 'PUT',
      }),
      invalidatesTags: ['CourtsBooking', 'Transactions']
    }),
    cancelCourtBooking: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/court_bookings/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['CourtsBooking', 'Transactions']
    }),
  }),
});

export const {
  useUpdateCourtBookingMutation,
  useCancelCourtBookingMutation,
  useConfirmCourtBookingMutation,
  useCreateCourtBookingMutation,
  useDeleteCourtBookingMutation,
  useGetCourtsBookingQuery,
  useLazyGetCourtBookingQuery,
  useAddUserToCourtBookingMutation,
} = api;
